import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { compareLocalized, readStoredSort, writeStoredSort } from '@/utils/sort';

export type SortDirection = 'asc' | 'desc';

type SortValue = string | number | null | undefined;
type SortAccessors<T> = Record<string, (row: T) => SortValue>;

interface UseTableSortOptions {
    /** When set, the active column + direction are persisted in localStorage under this key. */
    storageKey?: string
}

/**
 * Client-side column sorting for already-loaded table rows.
 *
 * `accessors` maps a column key to the value the column should sort on, so
 * derived columns (translated labels, joined strings, …) can sort on their
 * display value rather than the raw field. Pass `options.storageKey` to
 * remember the user's choice across reloads.
 */
export function useTableSort<T>(
    rows: Ref<T[]> | ComputedRef<T[]>,
    accessors: SortAccessors<T>,
    options: UseTableSortOptions = {},
) {
    const sortKey = ref<string | null>(null);
    const sortDirection = ref<SortDirection>('asc');

    if (options.storageKey) {
        const stored = readStoredSort(options.storageKey, (key) => Boolean(accessors[key]));
        if (stored) {
            sortKey.value = stored.key;
            sortDirection.value = stored.direction;
        }
    }

    const persist = (): void => {
        if (!options.storageKey) {
            return;
        }

        writeStoredSort(
            options.storageKey,
            sortKey.value ? { key: sortKey.value, direction: sortDirection.value } : null,
        );
    };

    // Cycle a column through ascending → descending → unsorted on repeated clicks.
    const toggleSort = (key: string): void => {
        if (sortKey.value !== key) {
            sortKey.value = key;
            sortDirection.value = 'asc';
        } else if (sortDirection.value === 'asc') {
            sortDirection.value = 'desc';
        } else {
            sortKey.value = null;
            sortDirection.value = 'asc';
        }

        persist();
    };

    const sorted = computed<T[]>(() => {
        const key = sortKey.value;
        const accessor = key ? accessors[key] : undefined;
        if (!accessor) {
            return rows.value;
        }

        const factor = sortDirection.value === 'asc' ? 1 : -1;

        return [...rows.value].sort((a, b) => compareLocalized(accessor(a), accessor(b)) * factor);
    });

    return { sortKey, sortDirection, toggleSort, sorted };
}
